import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Bike, Clock3, Flag, Link2, Mountain, Ruler, Trophy, Tv } from 'lucide-react';
import { ProRace, ProRaceStage, ProRaceClassificationEntry, ProRaceClassificationType, STAGE_TYPE_COLORS, displayBundesland, toExternalUrl } from '@radevents/shared';
import { buildSportsEventSchema } from '../hooks/useProRaceSchema';
import PublicHeader from '../components/Layout/PublicHeader';
import AppTopbarBrand from '../components/Layout/AppTopbarBrand';
import { RaceAccommodationCard } from '../components/AccommodationCard';
import api from '../services/api';
import { isRunning, daysUntilLabel } from '../utils/proRaceStatus';

import { SITE_ORIGIN } from '../config/siteOrigin';

const ICON_ROW: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5 };

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

function formatDateRange(start: string, end: string): string {
  // Date-only strings must be parsed as local time ('T00:00:00'), not UTC —
  // otherwise the calendar day shifts around local midnight.
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
  if (start === end) return s.toLocaleDateString('de-DE', opts);
  const startOpts: Intl.DateTimeFormatOptions = s.getMonth() === e.getMonth()
    ? { day: '2-digit' }
    : { day: '2-digit', month: 'long' };
  return `${s.toLocaleDateString('de-DE', startOpts)} – ${e.toLocaleDateString('de-DE', opts)}`;
}

function daysUntil(start: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(start + 'T00:00:00').getTime() - today.getTime()) / 86400000);
}

function isSameDay(d: string | undefined, ref: Date): boolean {
  if (!d) return false;
  const date = new Date(d + 'T00:00:00');
  return date.getFullYear() === ref.getFullYear()
    && date.getMonth() === ref.getMonth()
    && date.getDate() === ref.getDate();
}

type StageState = 'finished' | 'live' | 'upcoming';

/** Derive whether a stage is finished / live / upcoming from its date (+ times, if set). */
function stageState(stage: ProRaceStage): StageState {
  if (!stage.stageDate) return 'upcoming';
  const now = new Date();
  const d = new Date(stage.stageDate + 'T00:00:00');
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (day < today) return 'finished';
  if (day > today) return 'upcoming';
  // Same day — refine with times when available.
  const at = (t?: string) => {
    if (!t) return null;
    const [h, m] = t.split(':').map(Number);
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), h || 0, m || 0).getTime();
  };
  const arrival = at(stage.estimatedArrivalTime);
  if (arrival != null && now.getTime() > arrival) return 'finished';
  const start = at(stage.startTime);
  if (start != null && now.getTime() < start) return 'upcoming';
  return 'live';
}

const raceTypeLabel: Record<string, string> = {
  ETAPPENRENNEN: 'Etappenrennen',
  EINTAGESRENNEN: 'Eintagesrennen',
};

// Maps a German country name to its ISO 3166-1 alpha-2 code (in case enrichment
// stores full names); an already-2-letter value is used as-is.
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  Frankreich: 'FR', Italien: 'IT', Spanien: 'ES', Belgien: 'BE', Niederlande: 'NL',
  Deutschland: 'DE', Schweiz: 'CH', Österreich: 'AT', Großbritannien: 'GB',
  'Vereinigtes Königreich': 'GB', Luxemburg: 'LU', Dänemark: 'DK', Polen: 'PL',
  Slowenien: 'SI', Kroatien: 'HR', 'Vereinigte Arabische Emirate': 'AE',
  Australien: 'AU', Kanada: 'CA', Norwegen: 'NO', Schweden: 'SE', USA: 'US',
};

/** ISO alpha-2 country code for the badge, or null if unknown. */
function countryCode(country?: string): string | null {
  if (!country) return null;
  const c = country.trim();
  if (/^[A-Za-z]{2}$/.test(c)) return c.toUpperCase();
  return COUNTRY_NAME_TO_CODE[c] ?? null;
}

/** 🇫🇷-style flag emoji from an ISO 3166-1 alpha-2 code. */
function flagEmoji(cc?: string): string | null {
  if (!cc || !/^[A-Za-z]{2}$/.test(cc)) return null;
  const up = cc.toUpperCase();
  return String.fromCodePoint(0x1f1e6 + up.charCodeAt(0) - 65, 0x1f1e6 + up.charCodeAt(1) - 65);
}

/** Matches the app-prototype breakpoint (~390px phone frame; 640px covers phones). */
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 640px)').matches
      : false
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

type LbKey = 'zeit' | 'punkte' | 'berg';

const LB_DEFS: Record<LbKey, { label: string; shortLabel: string; metricLabel: string; jersey: string; tint: string; type: ProRaceClassificationType }> = {
  zeit:   { label: 'Zeit',        shortLabel: 'Zeit',   metricLabel: 'GESAMTZEIT', jersey: '#E0A100', tint: 'rgba(224,161,0,0.10)', type: 'GC' },
  punkte: { label: 'Sprint',      shortLabel: 'Sprint', metricLabel: 'PUNKTE',     jersey: '#1F8A5B', tint: 'rgba(31,138,91,0.10)', type: 'POINTS' },
  berg:   { label: 'Bergwertung', shortLabel: 'Berg',   metricLabel: 'BERG-PKT',   jersey: '#D23A2B', tint: 'rgba(210,58,43,0.09)', type: 'KOM' },
};
const LB_ORDER: LbKey[] = ['zeit', 'punkte', 'berg'];

const STAGE_TYPE_KEY: Record<string, string> = {
  FLACH: 'Flach', HÜGELIG: 'Hügelig', BERG: 'Bergetappe', ZEITFAHREN: 'Zeitfahren', PROLOG: 'Zeitfahren',
};
const stageTypeLabel: Record<string, string> = {
  FLACH: 'Flach', HÜGELIG: 'Hügelig', BERG: 'Bergetappe', ZEITFAHREN: 'Zeitfahren', PROLOG: 'Prolog',
};
function stageColor(t?: string): string {
  if (!t) return '#6A6A6A';
  return STAGE_TYPE_COLORS[STAGE_TYPE_KEY[t] ?? t] ?? '#6A6A6A';
}

function formatStageDate(d?: string) {
  return d ? new Date(d + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '–';
}

export default function ProRaceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  // Zurück zum Kalender: echtes History-Back (erhält Scrollposition/Filter),
  // sonst — bei Deeplink ohne In-App-Verlauf — auf den Rennkalender fallen lassen.
  const goBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/rennkalender');
  };
  const [race, setRace] = useState<ProRace | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [raceSection, setRaceSection] = useState<'etappen' | 'wertungen'>('etappen');
  const isMobile = useIsMobile();

  useEffect(() => { setRaceSection('etappen'); }, [slug]);

  useEffect(() => {
    if (!slug) return;
    window.scrollTo(0, 0);
    api.get(`/pro-races/${slug}`)
      .then(r => setRace(r.data))
      .catch(e => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ minHeight: '100vh', background: '#F5F3F3', padding: 48, textAlign: 'center', color: '#6A6A6A' }}>Lade…</div>;
  if (notFound || !race) return (
    <div style={{ minHeight: '100vh', background: '#F5F3F3', padding: 48, textAlign: 'center' }}>
      <p style={{ color: '#6A6A6A', marginBottom: 16 }}>Rennen nicht gefunden.</p>
      <Link to="/rennkalender" style={{ color: '#922C57' }}>← Zurück zum Kalender</Link>
    </div>
  );

  const running = isRunning(race.startDate, race.endDate);
  const days = daysUntil(race.startDate);
  const upcoming = days > 0;
  const dateRange = formatDateRange(race.startDate, race.endDate);
  const canonical = `${SITE_ORIGIN}/rennkalender/${race.slug}`;
  const pageTitle = `${race.name} 2026 – Termine, TV & Streaming`;
  const pageDesc = `${race.name} 2026: ${dateRange}. TV & Livestream: ${race.broadcastInfo ?? 'Eurosport, ARD'}. ${race.linkedEvents.length > 0 ? 'Auch für Hobbyfahrer!' : `${race.category ?? 'UCI WorldTour'}.`}`;

  // Läuft gerade → Grün + Live-Punkt mit Restdauer („Läuft noch X Tage"), wie in der Übersicht.
  const status: { label: string; color: string; bg: string } = running
    ? { label: `● ${daysUntilLabel(race.startDate, race.endDate) ?? 'Live'}`, color: '#1F8A5B', bg: 'rgba(31,138,91,0.12)' }
    : upcoming
      ? { label: days === 1 ? 'Morgen' : `In ${days} Tagen`, color: '#922C57', bg: '#F5F3F3' }
      : { label: 'Vergangen', color: '#9A9494', bg: '#F5F3F3' };

  const metaLine = [race.category, dateRange, raceTypeLabel[race.raceType] ?? race.raceType]
    .filter(Boolean).join(' · ');

  // Fall back to summing the stages when the stored totals are empty.
  const sumStageKm = race.stages.reduce((sum, s) => sum + (s.distanceKm ?? 0), 0);
  const sumStageHm = race.stages.reduce((sum, s) => sum + (s.elevationMeters ?? 0), 0);
  const totalKm = race.totalDistanceKm ?? (sumStageKm > 0 ? sumStageKm : null);
  // Nur Angaben mit Wert (30.10.2026): vorher standen „Gesamt –“ und „Höhenmeter –“ in eigenen
  // Kacheln, und „Umfang“ wiederholte die Rennart aus der Kopfzeile.
  const eckdaten = [
    race.stages.length > 0 ? { label: 'Etappen', value: String(race.stages.length) } : null,
    totalKm != null ? { label: 'Distanz', value: `${totalKm.toLocaleString('de-DE')} km` } : null,
    sumStageHm > 0 ? { label: 'Höhenmeter', value: `${sumStageHm.toLocaleString('de-DE')} hm` } : null,
  ].filter((x): x is { label: string; value: string } => !!x);
  const startD = new Date(`${race.startDate}T00:00:00`);
  const endD = new Date(`${race.endDate}T00:00:00`);
  const kachelTag = race.endDate !== race.startDate && startD.getMonth() === endD.getMonth()
    ? `${startD.getDate()}–${endD.getDate()}` : String(startD.getDate());
  const kachelMonat = startD.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');

  const hasStages = race.stages.length > 0;
  const isOneDay = !hasStages && race.raceType === 'EINTAGESRENNEN';

  // Wertungen only make sense for stage races and only once standings exist —
  // no dead "Wertungen" tab before the race has produced any.
  const availableLbKeys = LB_ORDER.filter(k =>
    (race.classifications ?? []).some(c => c.classificationType === LB_DEFS[k].type)
  );
  const hasWertungen = hasStages && availableLbKeys.length > 0;
  const showWertungen = hasWertungen && raceSection === 'wertungen';

  return (
    <div style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <script type="application/ld+json">
          {JSON.stringify(buildSportsEventSchema(race))}
        </script>
      </Helmet>

      <div className="hide-mobile">
        <PublicHeader />
      </div>

      {/* Phone-only app top bar: Zurück und Logo wie auf der Event-Seite. */}
      <div className="app-topbar hide-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44 }}>
        <AppTopbarBrand onBack={goBack} />
      </div>

      <div className="app-page" style={{ flex: 1, width: '100%', boxSizing: 'border-box', maxWidth: 1000, margin: '0 auto', padding: isMobile ? '14px 16px 48px' : '24px 24px 60px' }}>
        <nav aria-label="Breadcrumb" className="hide-mobile" style={{ fontFamily: FONT_MONO, fontSize: 12, color: '#6A6A6A', marginBottom: isMobile ? 12 : 18 }}>
          <button onClick={goBack} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: FONT_MONO, fontSize: 12, color: '#6A6A6A' }}>‹ RENNKALENDER</button>
          <span> / </span>
          <span style={{ color: '#2C2C2C' }}>{race.name}</span>
        </nav>

        {/* Kopf wie in der Kalenderliste: Datumskachel, Land und Kategorie, Titel. Statt der
            blassen Pille „Vergangen“ steht bei gelaufenen Rennen der Sieger (30.10.2026). */}
        <div className="rd-kopf">
          <span className="rk-kachel rd-kachel" aria-hidden>
            <span className="rk-kachel-monat">{kachelMonat}</span>
            <span className={kachelTag.length > 2 ? 'rk-kachel-tag rk-kachel-tag--spanne' : 'rk-kachel-tag'}>{kachelTag}</span>
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="rk-meta">
              {countryCode(race.country) && <span className="rk-land">{countryCode(race.country)}</span>}
              <time dateTime={race.startDate}>{metaLine}</time>
            </div>
            <h1 className="rd-titel">{race.name}</h1>
            {upcoming || running ? (
              <span className={running ? 'rk-status rk-status--live rd-status' : 'rk-status rd-status'}>
                {running && <span className="rk-live-punkt" aria-hidden />}
                {running ? (daysUntilLabel(race.startDate, race.endDate) ?? 'Läuft') : status.label}
              </span>
            ) : race.winner ? (
              <span className="rk-sieger rd-status"><Trophy size={15} aria-hidden />Sieger: {race.winner}</span>
            ) : null}
          </div>
        </div>

        {eckdaten.length > 0 && (
          <dl className="rd-eckdaten">
            {eckdaten.map(f => (
              <div key={f.label}><dt>{f.label}</dt><dd>{f.value}</dd></div>
            ))}
          </dl>
        )}

        {running && <TodayStageBanner race={race} />}

        {race.streamingProviders && race.streamingProviders.length > 0 && (
          <section aria-label="Wo streamen" style={{ marginTop: 28 }}>
            <div style={{ background: '#2C2C2C', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ ...ICON_ROW, fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.14em', color: '#C25078', padding: '16px 18px 12px' }}>
                <Tv size={12} aria-hidden /> WO STREAMEN
              </div>
              {race.streamingProviders.map(p => (
                <a
                  key={p.id}
                  href={`${api.defaults.baseURL}/pro-races/streaming/${p.id}/go`}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
                    borderTop: '1px solid rgba(255,255,255,0.07)', textDecoration: 'none',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: isMobile ? 14 : 16, color: '#fff' }}>{p.providerName}</span>
                      {p.isFree != null && (
                        <span style={{
                          fontFamily: FONT_MONO, fontWeight: 700, fontSize: 10, letterSpacing: '0.06em',
                          color: p.isFree ? '#1F8A5B' : '#E0A100',
                          background: p.isFree ? 'rgba(31,138,91,0.16)' : 'rgba(224,161,0,0.18)',
                          padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                        }}>
                          {p.isFree ? 'Kostenlos' : 'Abo'}
                        </span>
                      )}
                    </div>
                    {(p.note || p.isFree != null) && (
                      <div style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#C4BFBF', marginTop: 3 }}>
                        {p.note ?? (p.isFree ? 'Frei empfangbar · kein Abo' : 'Abo erforderlich')}
                      </div>
                    )}
                  </div>
                  <span style={{
                    flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontFamily: FONT_BODY, fontWeight: 700, fontSize: isMobile ? 13 : 14, color: '#2C2C2C',
                    background: '#C25078', padding: isMobile ? '9px 13px' : '10px 16px',
                    borderRadius: isMobile ? 9 : 10, whiteSpace: 'nowrap',
                  }}>
                    {isMobile ? 'Stream ↗' : 'Zum Stream ↗'}
                  </span>
                </a>
              ))}
            </div>
          </section>
        )}

        {hasStages && (
          <section aria-label="Etappen & Wertungen" style={{ marginTop: hasWertungen ? 0 : 28 }}>
            {hasWertungen ? (
              <SectionSwitcher
                stageCount={race.stages.length}
                section={raceSection}
                onChange={setRaceSection}
                isMobile={isMobile}
              />
            ) : (
              <div style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.12em', color: '#9A9494', margin: '0 0 12px' }}>
                ETAPPEN · {race.stages.length}
              </div>
            )}
            {showWertungen ? (
              <ClassificationsView
                classifications={race.classifications ?? []}
                availableKeys={availableLbKeys}
                isMobile={isMobile}
              />
            ) : (
              <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, overflow: 'hidden' }}>
                {race.stages.map((stage, i) => (
                  <StageRow key={stage.id} stage={stage} slug={race.slug} last={i === race.stages.length - 1} />
                ))}
              </div>
            )}
          </section>
        )}

        {isOneDay && (
          <div style={{ marginTop: 24, background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 22, fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.6, color: '#4A4A4A' }}>
            {race.description ?? `Eintagesklassiker${race.totalDistanceKm != null ? ` über ${race.totalDistanceKm.toLocaleString('de-DE')} km` : ''}, entschieden an einem einzigen Tag.`}
          </div>
        )}

        {race.description && !isOneDay && (
          <section aria-label="Beschreibung" style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: '20px 24px', marginTop: 24 }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.12em', color: '#2C2C2C', marginBottom: 12 }}>BESCHREIBUNG</div>
            <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.7, color: '#4A4A4A', margin: 0 }}>{race.description}</p>
          </section>
        )}

        {(race.websiteUrl || race.wikipediaUrl) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14, marginTop: 24 }}>
            {(race.websiteUrl || race.wikipediaUrl) && (
              <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 18 }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.1em', color: '#9A9494', marginBottom: 8, ...ICON_ROW }}><Link2 size={12} aria-hidden /> LINKS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {race.websiteUrl && <a href={toExternalUrl(race.websiteUrl)} target="_blank" rel="noopener noreferrer" style={{ color: '#922C57', fontFamily: FONT_BODY, fontWeight: 600, fontSize: 14 }}>Offizielle Website ↗</a>}
                  {race.wikipediaUrl && <a href={toExternalUrl(race.wikipediaUrl)} target="_blank" rel="noopener noreferrer" style={{ color: '#922C57', fontFamily: FONT_BODY, fontWeight: 600, fontSize: 14 }}>Wikipedia ↗</a>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Unterkunft — Partnerlink. Nur bei Eintagesrennen: eine Rundfahrt hat keinen Ort,
            den man buchen koennte, dort steht die Karte auf der Etappenseite. */}
        <div style={{ marginTop: 24 }}>
          <RaceAccommodationCard race={race} />
        </div>

        {race.linkedEvents.length > 0 && (
          <section aria-label="Events für Hobbyfahrer" style={{ background: 'rgba(31,138,91,0.06)', border: '1px solid rgba(31,138,91,0.2)', borderRadius: 14, padding: '20px 24px', marginTop: 24 }}>
            <h2 style={{ ...ICON_ROW, gap: 8, fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 800, margin: '0 0 6px', color: '#2C2C2C' }}>
              <Bike size={20} aria-hidden /> Auch für Hobbyfahrer
            </h2>
            <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: '#6A6A6A', margin: '0 0 16px' }}>
              Rund um dieses Profi-Rennen gibt es Veranstaltungen, bei denen du selbst mitfahren kannst:
            </p>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {race.linkedEvents.map(ev => (
                <li key={ev.id}>
                  <Link
                    to={`/events/${ev.id}`}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: '#fff', border: '1px solid #E5E1E1',
                      borderRadius: 10, padding: '12px 16px', textDecoration: 'none',
                    }}
                  >
                    <div>
                      <div style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, color: '#2C2C2C' }}>{ev.title}</div>
                      <div style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#6A6A6A', marginTop: 2 }}>
                        <time dateTime={ev.eventDate}>{new Date(ev.eventDate + 'T00:00:00').toLocaleDateString('de-DE')}</time>
                        {ev.city && ` · ${ev.city}`}
                        {ev.bundesland && `, ${displayBundesland(ev.bundesland)}`}
                      </div>
                      {ev.note && <div style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#9A9494', fontStyle: 'italic', marginTop: 2 }}>{ev.note}</div>}
                    </div>
                    <span style={{ color: '#C9C4C4', marginLeft: 12 }} aria-hidden="true">›</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

    </div>
  );
}

function SectionSwitcher({ stageCount, section, onChange, isMobile }: {
  stageCount: number;
  section: 'etappen' | 'wertungen';
  onChange: (s: 'etappen' | 'wertungen') => void;
  isMobile: boolean;
}) {
  const segments: Array<{ key: 'etappen' | 'wertungen'; label: string }> = [
    { key: 'etappen', label: `Etappen · ${stageCount}` },
    { key: 'wertungen', label: 'Wertungen' },
  ];
  const track = (
    <div role="tablist" aria-label="Etappen oder Wertungen" style={{
      display: 'flex', gap: 5, background: '#EAE6F2', borderRadius: 12, padding: 5,
      width: isMobile ? '100%' : 'fit-content',
      margin: isMobile ? 0 : '28px 0 16px',
    }}>
      {segments.map(seg => {
        const active = section === seg.key;
        return (
          <button
            key={seg.key}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(seg.key)}
            style={{
              cursor: 'pointer', border: 'none', borderRadius: 9,
              padding: isMobile ? '9px 6px' : '9px 20px',
              flex: isMobile ? 1 : undefined, textAlign: 'center',
              fontFamily: FONT_BODY, fontWeight: 700, fontSize: isMobile ? 13 : 14,
              background: active ? '#fff' : 'transparent',
              color: active ? '#2C2C2C' : '#6A6A6A',
              boxShadow: active ? '0 1px 3px rgba(38,38,38,0.12)' : 'none',
            }}
          >
            {seg.label}
          </button>
        );
      })}
    </div>
  );
  // App layout: the switcher stays reachable while scrolling long stage lists.
  if (isMobile) {
    return (
      <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#F5F3F3', padding: '18px 0 10px' }}>
        {track}
      </div>
    );
  }
  return track;
}

function ClassificationsView({ classifications, availableKeys, isMobile }: {
  classifications: ProRaceClassificationEntry[];
  availableKeys: LbKey[];
  isMobile: boolean;
}) {
  const [lbTab, setLbTab] = useState<LbKey>(availableKeys[0] ?? 'zeit');
  const activeKey = availableKeys.includes(lbTab) ? lbTab : (availableKeys[0] ?? 'zeit');
  const def = LB_DEFS[activeKey];
  const rows = classifications
    .filter(c => c.classificationType === def.type)
    .sort((a, b) => a.rank - b.rank);
  const asOfStage = rows.find(r => r.asOfStage != null)?.asOfStage;

  return (
    <div>
      {/* Classification tabs with jersey swatches */}
      <div role="tablist" aria-label="Wertung wählen" style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 14 }}>
        {availableKeys.map(k => {
          const d = LB_DEFS[k];
          const active = k === activeKey;
          return (
            <button
              key={k}
              role="tab"
              aria-selected={active}
              onClick={() => setLbTab(k)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                cursor: 'pointer',
                flex: isMobile ? 1 : undefined,
                padding: isMobile ? '9px 6px' : '9px 14px',
                borderRadius: 10, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13,
                background: active ? '#2C2C2C' : '#fff',
                color: active ? '#fff' : '#4A4A4A',
                border: `1px solid ${active ? '#2C2C2C' : '#E5E1E1'}`,
              }}
            >
              <span style={{
                width: isMobile ? 10 : 12, height: isMobile ? 12 : 14,
                borderRadius: isMobile ? 2 : 3, background: d.jersey, flexShrink: 0,
              }} />
              {isMobile ? d.shortLabel : d.label}
            </button>
          );
        })}
      </div>

      {/* Ranking table */}
      <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14,
          padding: isMobile ? '10px 14px' : '12px 20px',
          background: '#FAF7F7', borderBottom: '1px solid #EDE9E9',
          fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.1em', color: '#9A9494', textTransform: 'uppercase',
        }}>
          <div style={{ width: isMobile ? 20 : 30, flexShrink: 0 }}>#</div>
          <div style={{ flex: 1 }}>FAHRER</div>
          <div style={{ width: isMobile ? 90 : 120, textAlign: 'right', flexShrink: 0 }}>{def.metricLabel}</div>
        </div>
        {rows.map((r, i) => {
          const leader = i === 0;
          return (
            <div key={`${def.type}-${r.rank}`} style={{
              display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14,
              padding: isMobile ? '10px 14px' : '13px 20px',
              background: leader ? def.tint : '#fff',
              borderBottom: i === rows.length - 1 ? 'none' : '1px solid #EDE9E9',
            }}>
              <div style={{
                width: isMobile ? 20 : 30, flexShrink: 0,
                fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: isMobile ? 15 : 17,
                color: leader ? def.jersey : (i < 3 ? '#2C2C2C' : '#9A9494'),
              }}>
                {r.rank}
              </div>
              {flagEmoji(r.countryCode) && (
                <span style={{ fontSize: isMobile ? 17 : 20, flexShrink: 0, lineHeight: 1 }} aria-hidden="true">
                  {flagEmoji(r.countryCode)}
                </span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{
                    fontFamily: FONT_BODY, fontWeight: 700, fontSize: isMobile ? 13 : 15, color: '#2C2C2C',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {r.riderName}
                  </span>
                  {leader && (
                    <span
                      title={`Führt die ${def.label}-Wertung an`}
                      style={{
                        width: isMobile ? 10 : 12, height: isMobile ? 12 : 14,
                        borderRadius: isMobile ? 2 : 3, background: def.jersey, flexShrink: 0,
                      }}
                    />
                  )}
                </div>
                {r.teamName && (
                  <div style={{ fontFamily: FONT_BODY, fontSize: isMobile ? 11 : 12, color: '#6A6A6A', marginTop: 1 }}>
                    {r.teamName}
                  </div>
                )}
              </div>
              <div style={{
                width: isMobile ? 90 : 120, textAlign: 'right', flexShrink: 0,
                fontFamily: FONT_MONO, fontWeight: 700, fontSize: isMobile ? 12 : 14,
                color: leader ? def.jersey : '#2C2C2C',
              }}>
                {r.value ?? '–'}
              </div>
            </div>
          );
        })}
      </div>
      {asOfStage != null && (
        <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: '#9A9494', marginTop: 8 }}>
          Stand nach Etappe {asOfStage}
        </div>
      )}
    </div>
  );
}

function StageRoute({ stage }: { stage: ProRaceStage }) {
  if (stage.startCity && stage.endCity) {
    return <>{stage.startCity} <span style={{ color: '#9A9494' }}>→</span> {stage.endCity}</>;
  }
  return <>{stage.startCity || stage.endCity || '–'}</>;
}

function StageRow({ stage, slug, last }: { stage: ProRaceStage; slug: string; last: boolean }) {
  const state = stageState(stage);
  const times = stage.startTime
    ? `${stage.startTime}${stage.estimatedArrivalTime ? ` – ${stage.estimatedArrivalTime}` : ''} Uhr`
    : null;

  return (
    <Link
      to={`/rennkalender/${slug}/etappe/${stage.stageNumber}`}
      style={{
        display: 'block', padding: '14px 18px', borderBottom: last ? 'none' : '1px solid #EDE9E9',
        textDecoration: 'none', cursor: 'pointer',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = '#FAF7F7')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13 }}>
        <div style={{ width: 24, flexShrink: 0, fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 16, color: '#922C57', lineHeight: 1.35 }}>
          {stage.stageNumber === 0 ? 'P' : stage.stageNumber}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Route + live badge / date */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 15, color: '#2C2C2C', minWidth: 0 }}>
              <StageRoute stage={stage} />
            </span>
            {state === 'live' ? (
              <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: FONT_MONO, fontWeight: 700, fontSize: 11, letterSpacing: '0.04em', color: '#A82F5A', background: 'rgba(194,80,120,0.12)', padding: '4px 9px', borderRadius: 999 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#A82F5A' }} />
                JETZT LIVE
              </span>
            ) : (
              <span style={{ flexShrink: 0, fontFamily: FONT_MONO, fontSize: 12, color: '#6A6A6A' }}>{formatStageDate(stage.stageDate)}</span>
            )}
          </div>
          {/* Type · distance · elevation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginTop: 6 }}>
            {stage.stageType && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: FONT_BODY, fontSize: 13, color: '#4A4A4A' }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: stageColor(stage.stageType), flexShrink: 0 }} />
                {stageTypeLabel[stage.stageType] ?? stage.stageType}
              </span>
            )}
            <span style={{ fontFamily: FONT_MONO, fontSize: 13, color: '#6A6A6A' }}>
              {stage.distanceKm != null ? `${stage.distanceKm} km` : '–'}
              {stage.elevationMeters != null ? ` · ${stage.elevationMeters.toLocaleString('de-DE')} hm` : ''}
            </span>
          </div>
          {/* Winner (finished) or start/finish times (upcoming/live) */}
          {state === 'finished' && stage.winner ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 7, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, color: '#2C2C2C' }}>
              <Trophy size={13} aria-hidden /> {stage.winner}
            </div>
          ) : state !== 'finished' && times ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 7, fontFamily: FONT_MONO, fontSize: 12, color: state === 'live' ? '#A82F5A' : '#6A6A6A' }}>
              <Clock3 size={13} aria-hidden /> {times}
            </div>
          ) : null}
        </div>
        <span style={{ color: '#C9C4C4', fontSize: 18, flexShrink: 0, alignSelf: 'center' }} aria-hidden="true">›</span>
      </div>
    </Link>
  );
}

function TodayStageBanner({ race }: { race: ProRace }) {
  const today = new Date();
  const todayStage = [...race.stages].sort((a, b) => a.stageNumber - b.stageNumber)
    .find(s => isSameDay(s.stageDate, today));

  if (!todayStage) {
    return (
      <div style={{ background: 'rgba(31,138,91,0.08)', border: '1px solid rgba(31,138,91,0.25)', borderRadius: 14, padding: '14px 18px', marginTop: 24 }}>
        <span style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 700, color: '#1F8A5B' }}>● LÄUFT GERADE</span>
        {race.broadcastInfo && <span style={{ fontFamily: FONT_BODY, fontSize: 14, color: '#6A6A6A', marginLeft: 10, ...ICON_ROW, verticalAlign: 'middle' }}><Tv size={14} aria-hidden /> {race.broadcastInfo}</span>}
      </div>
    );
  }

  const label = todayStage.stageNumber === 0 ? 'Prolog' : `${todayStage.stageNumber}. Etappe`;
  return (
    <Link
      to={`/rennkalender/${race.slug}/etappe/${todayStage.stageNumber}`}
      style={{
        display: 'block', textDecoration: 'none',
        background: 'rgba(31,138,91,0.08)', border: '1px solid rgba(31,138,91,0.3)',
        borderRadius: 14, padding: '16px 20px', marginTop: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 700, color: '#1F8A5B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>● Heute live</span>
        {todayStage.stageType && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: FONT_BODY, fontSize: 12, color: '#6A6A6A' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: stageColor(todayStage.stageType) }} />
            {stageTypeLabel[todayStage.stageType] ?? todayStage.stageType}
          </span>
        )}
      </div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 800, color: '#2C2C2C', marginBottom: 4 }}>
        {label}: <StageRoute stage={todayStage} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontFamily: FONT_BODY, fontSize: 13, color: '#6A6A6A' }}>
        {todayStage.distanceKm != null && <span style={ICON_ROW}><Ruler size={14} aria-hidden /> {todayStage.distanceKm} km</span>}
        {todayStage.elevationMeters != null && <span style={ICON_ROW}><Mountain size={14} aria-hidden /> {todayStage.elevationMeters.toLocaleString('de-DE')} hm</span>}
        {todayStage.startTime && <span style={ICON_ROW}><Flag size={14} aria-hidden /> Start {todayStage.startTime}</span>}
        {race.broadcastInfo && <span style={ICON_ROW}><Tv size={14} aria-hidden /> {race.broadcastInfo}</span>}
      </div>
    </Link>
  );
}
